import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  DollarSign,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Eye,
  Check,
  X,
  CreditCard,
  Building2,
  Calendar,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatCard = ({ title, value, icon: Icon, color, prefix = '' }) => (
  <div className="bg-card rounded-lg border border-border p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold mt-1">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
      </div>
      <div className={cn('p-3 rounded-lg', color)}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const config = {
    pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Pending' },
    approved: { icon: AlertCircle, color: 'bg-blue-100 text-blue-700', label: 'Approved' },
    completed: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Completed' },
    rejected: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Rejected' },
  };
  const { icon: StatusIcon, color, label } = config[status] || config.pending;

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', color)}>
      <StatusIcon className="w-3 h-3" />
      {label}
    </span>
  );
};

const WithdrawalsPage = () => {
  const { user } = useCompanyAdminAuth();
  const isAgent = Number(user?.user_type) === 3;

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [withdrawalDetail, setWithdrawalDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form state
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const res = await companyAdminCustomersAPI.getWithdrawalStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };
      const res = await companyAdminCustomersAPI.getWithdrawals(params);
      setWithdrawals(res.data.withdrawals || []);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        pages: res.data.pages,
      }));
    } catch (error) {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter]);

  useEffect(() => {
    loadStats();
    loadWithdrawals();
  }, [loadStats, loadWithdrawals]);

  const handleViewDetail = async (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setDetailModalOpen(true);
    try {
      const res = await companyAdminCustomersAPI.getWithdrawalDetail(withdrawal.withdrawal_id);
      setWithdrawalDetail(res.data.withdrawal);
    } catch (error) {
      toast.error('Failed to load withdrawal details');
    }
  };

  const handleApprove = async (withdrawal) => {
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.approveWithdrawal(withdrawal.withdrawal_id);
      toast.success('Withdrawal approved');
      loadWithdrawals();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenComplete = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setTransactionId('');
    setNotes('');
    setCompleteModalOpen(true);
  };

  const handleComplete = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.completeWithdrawal(selectedWithdrawal.withdrawal_id, {
        transaction_id: transactionId,
        notes,
      });
      toast.success('Withdrawal completed');
      setCompleteModalOpen(false);
      loadWithdrawals();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReject = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.rejectWithdrawal(selectedWithdrawal.withdrawal_id, {
        reason: rejectionReason,
      });
      toast.success('Withdrawal rejected');
      setRejectModalOpen(false);
      loadWithdrawals();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Partner Withdrawals</h1>
            <p className="text-muted-foreground mt-1">Manage partner payout requests</p>
          </div>
          <Button onClick={() => { loadStats(); loadWithdrawals(); }} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard title="Pending" value={stats.pending_count} icon={Clock} color="bg-amber-500" />
            <StatCard title="Approved" value={stats.approved_count} icon={AlertCircle} color="bg-blue-500" />
            <StatCard title="Completed" value={stats.completed_count} icon={CheckCircle} color="bg-green-500" />
            <StatCard title="Rejected" value={stats.rejected_count} icon={XCircle} color="bg-red-500" />
            <StatCard title="Pending Amount" value={formatCurrency(stats.pending_amount)} icon={DollarSign} color="bg-amber-600" />
            <StatCard title="Total Paid" value={formatCurrency(stats.completed_amount)} icon={DollarSign} color="bg-green-600" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-[200px]">
              <Label className="text-sm mb-1.5 block">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Withdrawals Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Partner</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Amount</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Payment Method</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Requested</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Status</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : withdrawals.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No withdrawals found</td></tr>
                ) : (
                  withdrawals.map((withdrawal) => (
                    <tr key={withdrawal.withdrawal_id} className="border-b border-border hover:bg-muted">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{withdrawal.partner_ref?.partner_name || 'Partner'}</p>
                          <p className="text-sm text-muted-foreground">{withdrawal.partner_ref?.company_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-lg">{formatCurrency(withdrawal.amount)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-muted-foreground" />
                          <span className="capitalize">{withdrawal.payment_method?.replace(/_/g, ' ')}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{formatDate(withdrawal.requested_at)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={withdrawal.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetail(withdrawal)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {!isAgent && withdrawal.status === 'pending' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleApprove(withdrawal)}>
                                  <Check className="w-4 h-4 mr-2" /> Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenComplete(withdrawal)}>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Mark as Paid
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenReject(withdrawal)} className="text-destructive">
                                  <X className="w-4 h-4 mr-2" /> Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            {!isAgent && withdrawal.status === 'approved' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleOpenComplete(withdrawal)}>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Mark as Paid
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenReject(withdrawal)} className="text-destructive">
                                  <X className="w-4 h-4 mr-2" /> Reject
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Withdrawal Details</DialogTitle>
            <DialogDescription>
              Request from {withdrawalDetail?.partner_ref?.partner_name}
            </DialogDescription>
          </DialogHeader>
          {withdrawalDetail ? (
            <div className="space-y-4">
              <div className="text-center py-4 bg-muted rounded-lg">
                <p className="text-4xl font-bold">{formatCurrency(withdrawalDetail.amount)}</p>
                <StatusBadge status={withdrawalDetail.status} />
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Partner</p>
                  <p className="font-medium">{withdrawalDetail.partner_ref?.partner_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Company</p>
                  <p className="font-medium">{withdrawalDetail.partner_ref?.company_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment Method</p>
                  <p className="font-medium capitalize">{withdrawalDetail.payment_method?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Requested At</p>
                  <p className="font-medium">{formatDateTime(withdrawalDetail.requested_at)}</p>
                </div>
              </div>

              {withdrawalDetail.payment_details && (
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Payment Details</h4>
                  <div className="text-sm space-y-1">
                    {withdrawalDetail.payment_details.bank_name && (
                      <p>Bank: {withdrawalDetail.payment_details.bank_name}</p>
                    )}
                    {withdrawalDetail.payment_details.account_number && (
                      <p>Account: {withdrawalDetail.payment_details.account_number}</p>
                    )}
                    {withdrawalDetail.payment_details.paypal_email && (
                      <p>PayPal: {withdrawalDetail.payment_details.paypal_email}</p>
                    )}
                    {withdrawalDetail.payment_details.transaction_id && (
                      <p>Transaction ID: {withdrawalDetail.payment_details.transaction_id}</p>
                    )}
                  </div>
                </div>
              )}

              {withdrawalDetail.rejection_reason && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-red-700 mb-1">Rejection Reason</h4>
                  <p className="text-sm text-red-600">{withdrawalDetail.rejection_reason}</p>
                </div>
              )}

              {withdrawalDetail.processed_at && (
                <div className="text-sm text-muted-foreground">
                  Processed: {formatDateTime(withdrawalDetail.processed_at)}
                  {withdrawalDetail.processed_by && ` by ${withdrawalDetail.processed_by.name || withdrawalDetail.processed_by.email}`}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Modal */}
      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
            <DialogDescription>
              Confirm payment of {formatCurrency(selectedWithdrawal?.amount)} to {selectedWithdrawal?.partner_ref?.partner_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Transaction ID (optional)</Label>
              <Input
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Enter payment transaction ID"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>Cancel</Button>
            <Button onClick={handleComplete} disabled={actionLoading}>
              {actionLoading ? 'Processing...' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Withdrawal</DialogTitle>
            <DialogDescription>
              Reject withdrawal request of {formatCurrency(selectedWithdrawal?.amount)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Rejection Reason</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading}>
              {actionLoading ? 'Rejecting...' : 'Reject Withdrawal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CompanyAdminLayout>
  );
};

export default WithdrawalsPage;
